import React, { useState, useEffect, useCallback } from 'react'
import { XMarkIcon, TruckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import falabellaService from '../../api/falabellaService'
import { FalabellaTrackingData } from '../../types/falabella'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'

interface FalabellaTrackingModalProps {
  isOpen: boolean
  onClose: () => void
  ordersId: string
  orderNumber?: string
}


const FalabellaTrackingModal: React.FC<FalabellaTrackingModalProps> = ({
  isOpen,
  onClose,
  ordersId,
  orderNumber
}) => {
  const [trackingData, setTrackingData] = useState<FalabellaTrackingData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const handleGetTracking = useCallback(async () => {
    if (!ordersId.trim()) {
      toast.error('ID de pedido es requerido')
      return
    }

    setIsLoading(true)
    setError(null)
    setTrackingData(null)

    try {
      const response = await falabellaService.getTrackingByOrderId(
        ordersId.trim(),
        'ibis' // Siempre usar ibis como proveedor
      )

      if (response.success && response.data) {
        setTrackingData(response.data)
        if (!isInitialLoad) {
          toast.success('Número de seguimiento obtenido exitosamente')
        }
      } else {
        setError(response.message || 'No se pudo obtener el número de seguimiento')
        if (!isInitialLoad) {
          toast.error(response.message || 'Error al obtener el seguimiento')
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error al consultar el seguimiento'
      setError(errorMessage)
      if (!isInitialLoad) {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
      setIsInitialLoad(false)
    }
  }, [ordersId, isInitialLoad])

  // Consultar automáticamente al abrir el modal
  useEffect(() => {
    if (isOpen && ordersId.trim()) {
      handleGetTracking()
    }
  }, [isOpen, ordersId, handleGetTracking])

  const handleClose = () => {
    setTrackingData(null)
    setError(null)
    setIsInitialLoad(true)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <TruckIcon className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Seguimiento Falabella
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Order Info */}
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-blue-800">Pedido:</span>
                <span className="text-sm text-blue-600 font-mono">
                  {orderNumber || ordersId}
                </span>
              </div>
              <div className="text-xs text-blue-600">
                ID de pedido: {ordersId}
              </div>
              <div className="text-xs text-blue-600 mt-1">
                Proveedor: ibis
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="mb-6">
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <LoadingSpinner size="large" color="text-blue-600" />
                  <p className="text-sm text-gray-600 mt-2">
                    Consultando número de seguimiento...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Button - Solo mostrar si no está cargando inicialmente */}
          {!isLoading && (
            <div className="mb-6">
              <button
                onClick={handleGetTracking}
                disabled={!ordersId.trim()}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <TruckIcon className="h-4 w-4" />
                Consultar Nuevamente
              </button>
            </div>
          )}

          {/* Results */}
          {error && (
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                  <span className="text-sm font-medium text-red-800">Error</span>
                </div>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {trackingData && (
            <div className="mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TruckIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-medium text-green-800">
                    Información de Seguimiento
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <span className="text-xs font-medium text-green-700 block">Número de Seguimiento:</span>
                    <span className="text-lg font-mono font-bold text-green-800">
                      {trackingData.trackingNumber}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-xs font-medium text-green-700 block">Proveedor:</span>
                    <span className="text-sm text-green-800">
                      {trackingData.shippingProvider}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-xs font-medium text-green-700 block">Items en el pedido:</span>
                    <span className="text-sm text-green-800">
                      {trackingData.itemsCount}
                    </span>
                  </div>
                  
                  {trackingData.orderItemIds.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-green-700 block">IDs de Items:</span>
                      <div className="text-xs text-green-600 font-mono">
                        {trackingData.orderItemIds.join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default FalabellaTrackingModal
